import api from "@/app/api";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import { EnumBmEntityType } from "@/app/services/generated/enums/enumBmEntityType.generated";
import { EnumCustomerHappinessRating } from "@/app/services/generated/enums/enumCustomerHappinessRating.generated";
import { outletCommunicationLogService } from "@/app/services/generated/outletcommunicationlogService";
import Guid from "@/app/types/common/guid";

import userStore, { requireUserStoreUser } from "@/store/modules/userStore";
import type { Instant } from "@js-joda/core";
import { BrewManError } from "@/app/errors";
import { leadCommunicationLogService } from "@/app/services/generated/leadcommunicationlogService";
import type { CommunicationSubject } from "@/app/services/generated/communicationsubjectService";
import type { CommunicationType } from "@/app/services/generated/communicationtypeService";
import {
  SupplierManufacturerCommunicationLogEntry,
  supplierManufacturerCommunicationLogService,
} from "@/app/services/generated/suppliermanufacturercommunicationlogService";

export class CommunicationLogEntry {
  communicationTypeId: Guid;
  communicationSubjectId: Guid;
  timestamp: Instant;
  customerHappinessRating: EnumCustomerHappinessRating;
  description: string;
  entityId: Guid | null;
  id: Guid;

  constructor(entityId: Guid | null) {
    this.communicationSubjectId = Guid.createEmpty();
    this.communicationTypeId = Guid.createEmpty();
    this.timestamp = tenantService.now;
    this.customerHappinessRating = EnumCustomerHappinessRating.neutral;
    this.description = "";
    this.entityId = entityId;
    this.id = Guid.createEmpty();
  }

  clone(this: CommunicationLogEntry) {
    const clone = new CommunicationLogEntry(this.id);
    // All the properties are simple immutable types, so can simply use object.assign to copy them
    Object.assign(clone, this);
    return clone;
  }
}

export const addCommunicationLogEntry = async (opts: {
  communicationLogEntry: CommunicationLogEntry;
  entityType: EnumBmEntityType;
}) => {
  const { communicationLogEntry, entityType } = opts;

  if (!communicationLogEntry.entityId) {
    throw new BrewManError("Entity id must be set");
  }

  requireUserStoreUser(userStore.state.user);

  let communicationLogEntryId: Guid | null = null;

  switch (entityType) {
    case EnumBmEntityType.outlet:
      communicationLogEntryId = (
        await outletCommunicationLogService.addOutletLogEntry(
          api.tenantId,
          communicationLogEntry.communicationSubjectId,
          communicationLogEntry.communicationTypeId,
          communicationLogEntry.customerHappinessRating,
          communicationLogEntry.description,
          communicationLogEntry.entityId,
          userStore.state.user.id,
          communicationLogEntry.timestamp
        )
      ).id;
      break;

    case EnumBmEntityType.lead:
      communicationLogEntryId = (
        await leadCommunicationLogService.addLeadLogEntry(
          api.tenantId,
          communicationLogEntry.communicationSubjectId,
          communicationLogEntry.communicationTypeId,
          communicationLogEntry.customerHappinessRating,
          communicationLogEntry.description,
          communicationLogEntry.entityId,
          userStore.state.user.id,
          communicationLogEntry.timestamp
        )
      ).id;
      break;

    case EnumBmEntityType.supplier:
      {
        const logEntry = new SupplierManufacturerCommunicationLogEntry();
        logEntry.id = Guid.create();
        logEntry.communicationSubjectId =
          communicationLogEntry.communicationSubjectId;
        logEntry.communicationTypeId =
          communicationLogEntry.communicationTypeId;
        logEntry.customerHappinessRating =
          communicationLogEntry.customerHappinessRating;
        logEntry.description = communicationLogEntry.description;
        logEntry.supplierManufacturerId = communicationLogEntry.entityId;
        logEntry.userId = userStore.state.user.id;
        logEntry.timestamp = communicationLogEntry.timestamp;

        communicationLogEntryId = (
          await supplierManufacturerCommunicationLogService.addSupplierManufacturerLogEntry(
            api.tenantId,
            logEntry
          )
        ).id;
      }
      break;

    default:
      throw new BrewManError(
        `Unsupported entity type ${entityType.translation}`
      );
  }

  return communicationLogEntryId;
};

export const createCommunicationLogEntryWithDefaults = ({
  communicationSubjects,
  communicationTypes,
  entityId = null,
}: {
  communicationSubjects: readonly CommunicationSubject[];
  communicationTypes: readonly CommunicationType[];
  entityId?: Guid | null;
}) => {
  const communicationLogEntry = new CommunicationLogEntry(entityId);

  if (communicationSubjects.length > 0) {
    communicationLogEntry.communicationSubjectId = communicationSubjects[0].id;
  }

  if (communicationTypes.length > 0) {
    communicationLogEntry.communicationTypeId = communicationTypes[0].id;
  }

  return communicationLogEntry;
};
